import { Group, Object3D, Object3DEventMap } from "three";
import { useRef, useState } from "react";
import { animated, useSpring } from "@react-spring/three";
import { config } from "@react-spring/web";
import { useFrame } from "@react-three/fiber";
import { Float, RoundedBox } from "@react-three/drei";
import { useEditorStore } from "../stores/editorStore.ts";
import EditorPrototype from "../EditorPrototype.tsx";

type PrototypeCardProps = {
  model: Object3D;
};

export function PrototypeCard({ model }: PrototypeCardProps) {
  const cardRef = useRef<Group<Object3DEventMap>>(null);

  let [activeCard, setActiveCard] = useState(false);
  let [isSelected, setIsSelected] = useState(false);
  let [rotation, setRotation] = useState(0);

  const setSelectedPrototype = useEditorStore(
    (state) => state.setSelectedPrototype,
  );
  const selectedPrototypeSub = useEditorStore.subscribe(
    (state) => state.selectedPrototype,
    (selected) => {
      if (selected === null) {
        setIsSelected(false);
      } else {
        setIsSelected(String(model.id) === selected.id);
      }
    },
  );

  const { scale } = useSpring({
    scale: activeCard || isSelected ? (isSelected ? 1.15 : 1.05) : 1,
    config: config.wobbly,
  });

  useFrame(() => {
    if (activeCard || isSelected) {
      setRotation((prev) => (prev + 0.01) % (Math.PI * 2));
    } else if (rotation > 0) {
      setRotation((prev) => (prev - 0.05) % (Math.PI * 2)); // TODO : smooth back animation
    }
  });

  return (
    <animated.group
      ref={cardRef}
      scale={scale}
      onPointerDown={(e) => {
        e.stopPropagation();
        if (isSelected) {
          setSelectedPrototype(null);
        } else {
          setSelectedPrototype(
            new EditorPrototype(String(model.id), model.name, 0),
          );
        }
      }}
      onPointerEnter={(e) => {
        setActiveCard(true);
      }}
      onPointerLeave={(e) => {
        setActiveCard(false);
      }}
    >
      <RoundedBox
        args={[3, 4, 1.1]}
        radius={0.5}
        bevelSegments={0}
        position={[0, 0, -1.05]}
      >
        <meshBasicMaterial color="white" />
      </RoundedBox>

      <Float speed={3} rotationIntensity={0.2} floatIntensity={1}>
        <primitive
          rotation={[0, rotation, 0]}
          object={model.clone()}
          scale={[2, 2, 2]}
        />
      </Float>
    </animated.group>
  );
}
